import { Transaction, EditorState } from 'prosemirror-state'
import { AddMarkStep, Mapping } from 'prosemirror-transform'
import dayjs from 'dayjs'
import { TRACKABLE_MARKS } from './utils'
import { ourUuid } from 'utils'
import { Context } from './Types'

/**
 * Wrap an AddMarkStep and keep track of which marks are added to the changed
 * range. We keep track of the added marks in the array `after` of the
 * `formatting_change` mark.
 *
 * @param step - the step to track
 * @param userId - the id of the user making the change
 * @param state - the state of the editor
 * @param tr - the wrapped transaction. NOTE: This is not the original transaction
 * @param map - the mapping of the wrapped transaction
 * @param map - the mapping of the wrapped transaction
 * @param context - the context containing metadata and hints for the tracked changes handling
 */
export function trackAddMarkStep(
  step: AddMarkStep,
  userId: number,
  state: EditorState,
  tr: Transaction,
  map: Mapping,
  context: Context,
) {
  const mappedFrom = map.map(step.from)
  const mappedTo = map.map(step.to)
  tr.doc.nodesBetween(mappedFrom, mappedTo, (node, pos) => {
    // Block nodes do not support marks
    if (!node.isInline) {
      return true
    }

    const start = Math.max(mappedFrom, pos)
    const end = Math.min(mappedTo, pos + node.nodeSize)
    const addedMarkName = step.mark.type.name

    // Ignore tracked deletions
    if (node.marks.find((mark) => mark.type === state.schema.marks.deletion)) {
      return false
    }

    tr.addMark(start, end, step.mark)

    if (
      TRACKABLE_MARKS.includes(addedMarkName) &&
      !node.marks.find(({ type }) => {
        return type.name === addedMarkName || type === state.schema.marks.insertion
      })
    ) {
      const formattingChangeMark = node.marks.find(({ type }) => type === state.schema.marks.formatting_change)
      const createdAt = formattingChangeMark ? formattingChangeMark.attrs.createdAt : dayjs().toISOString()
      const uuid = context?.uuid || formattingChangeMark?.attrs.uuid || ourUuid()

      let after: any
      let before: any

      if (formattingChangeMark) {
        const { after: currentAfter, before: currentBefore } = formattingChangeMark.attrs

        if (currentBefore.includes(addedMarkName)) {
          before = currentBefore.filter((markName: string) => markName !== addedMarkName)
          after = currentAfter
        } else {
          before = currentBefore
          after = currentAfter.includes(addedMarkName) ? currentAfter : currentAfter.concat(addedMarkName)
        }
      } else {
        before = []
        after = [addedMarkName]
      }

      if (after.length || before.length) {
        tr.addMark(start, end, state.schema.marks.formatting_change.create({ userId, createdAt, before, after, uuid }))
      } else if (formattingChangeMark) {
        tr.removeMark(start, end, formattingChangeMark)
      }
    }
  })
}
